import React, { Component } from 'react';
import { connect } from 'react-redux';
import { CheckCircleTwoTone, CloseCircleTwoTone, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Divider, Modal, Select, Tooltip } from 'antd';
import HtmlParser from 'react-html-parser';
import qs from 'qs';
import PageTitle from '@/components/PageTitle';
import TableActionBar from '@/components/TableActionBar';
import DynamicTable from '@/components/DynamicTable';
import CreatedAtCell from '@/components/CreatedAtCell';
import { checkPermission } from '@/utils/permission';
import { cleanObject, stripHtml } from '@/utils/helpers';
import {
    getListDocumentCategories,
    getSpecificDocumentCategories,
    removeDocumentCategories
} from '@/redux/actions/documentCategorieActions';
import DocumentCategoryForm from './DocumentCategoryForm';

const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: '/document-categories', breadcrumbName: 'Danh mục văn bản' }
];

class DocumentCategoryList extends Component {
    state = { isLoading: false, selectedRowKeys: [], visible: false, currentItem: null };

    componentDidMount() {
        this.loadData(this.getQuery());
    }

    componentDidUpdate(prevProps) {
        if (prevProps.location.search !== this.props.location.search) {
            this.loadData(this.getQuery());
        }
    }

    getQuery = () => qs.parse(this.props.location.search, { ignoreQueryPrefix: true });

    loadData = (query) => {
        this.setState({ isLoading: true });
        return this.props.getListDocumentCategories(query)
            .finally(() => this.setState({ isLoading: false }));
    };

    changeFilter = (name, value) => {
        const query = cleanObject({ ...this.getQuery(), [name]: value, page: 1 });
        this.props.history.push({ pathname: this.props.location.pathname, search: qs.stringify(query) });
    };

    edit = (id) => {
        this.props.getSpecificDocumentCategories(id).then(currentItem => {
            this.setState({ visible: true, currentItem });
        });
    };

    remove = (ids) => {
        this.props.removeDocumentCategories(ids).then(() => {
            this.setState({ selectedRowKeys: [] });
            this.loadData(this.getQuery());
        });
    };

    confirmRemove = (ids) => Modal.confirm({
        title: 'Xóa danh mục văn bản',
        content: 'Bạn chắc chắn muốn xóa các danh mục đã chọn?',
        okText: 'Xóa',
        okType: 'danger',
        cancelText: 'Hủy',
        onOk: () => this.remove(ids)
    });

    render() {
        const query = this.getQuery();
        const { documentCategories, pagination } = this.props;
        const columns = [
            { title: 'ID', dataIndex: 'id', key: 'id', sorter: true, width: 70 },
            {
                title: 'Thao tác', key: 'actions', width: 90, align: 'center', render: (_, record) => (
                    <>
                        {checkPermission('document_categories', 'update') && (
                            <EditOutlined className="color-primary" onClick={() => this.edit(record.id)} />
                        )}
                        {checkPermission('document_categories', 'update') && checkPermission('document_categories', 'remove') && <Divider type="vertical" />}
                        {checkPermission('document_categories', 'remove') && (
                            <DeleteOutlined style={{ color: 'red' }} onClick={() => this.confirmRemove([record.id])} />
                        )}
                    </>
                )
            },
            { title: 'Tên danh mục', dataIndex: 'name', key: 'name', sorter: true, width: 240 },
            {
                title: 'Ghi chú', dataIndex: 'note', key: 'note', width: 300,
                render: value => <Tooltip title={HtmlParser(value || '')}><span>{stripHtml(value || '') || '—'}</span></Tooltip>
            },
            {
                title: 'Ngày tạo', dataIndex: 'created_at', key: 'created_at', sorter: true, width: 220,
                render: (value, record) => <CreatedAtCell createdAt={value} user={record.created_user} />
            },
            {
                title: 'Trạng thái', dataIndex: 'status', key: 'status', align: 'center', width: 110,
                render: value => Number(value) === 1
                    ? <CheckCircleTwoTone twoToneColor="#52c41a" />
                    : <CloseCircleTwoTone twoToneColor="#e74c3c" />
            }
        ];

        return (
            <div>
                <PageTitle routes={routes} title="Danh mục văn bản" />
                <TableActionBar
                    isShowAddButton={checkPermission('document_categories', 'create')}
                    isShowDeleteButton={checkPermission('document_categories', 'remove')}
                    disabled={!this.state.selectedRowKeys.length}
                    onAdd={() => this.setState({ visible: true, currentItem: null })}
                    onDelete={() => this.confirmRemove(this.state.selectedRowKeys)}
                    onSearch={value => this.changeFilter('keyword', value)}
                    defaultKeyword={query.keyword}
                    activeFilter={query.status !== undefined}
                    filters={[
                        <Select
                            key="status"
                            allowClear
                            placeholder="Trạng thái"
                            style={{ width: '100%' }}
                            value={query.status !== undefined ? Number(query.status) : undefined}
                            onChange={value => this.changeFilter('status', value)}
                        >
                            <Select.Option value={1}>Kích hoạt</Select.Option>
                            <Select.Option value={0}>Ngừng sử dụng</Select.Option>
                        </Select>
                    ]}
                >
                    {this.state.selectedRowKeys.length > 0 && <span>Đang chọn {this.state.selectedRowKeys.length} bản ghi</span>}
                </TableActionBar>
                <div className="has-custom-column-table-btn">
                    <DynamicTable
                        moduleKey="document_category_list"
                        columns={columns}
                        dataSource={documentCategories}
                        isLoading={this.state.isLoading}
                        pagination={pagination}
                        rowSelection={{
                            selectedRowKeys: this.state.selectedRowKeys,
                            onChange: selectedRowKeys => this.setState({ selectedRowKeys })
                        }}
                    />
                </div>
                <DocumentCategoryForm
                    visible={this.state.visible}
                    currentItem={this.state.currentItem}
                    onCancel={() => this.setState({ visible: false, currentItem: null })}
                    reloadData={() => this.loadData(this.getQuery())}
                />
            </div>
        );
    }
}

const mapStateToProps = state => ({
    documentCategories: state.documentCategorieReducer.document_categories,
    pagination: state.documentCategorieReducer.pagination
});

const mapDispatchToProps = dispatch => ({
    getListDocumentCategories: filter => dispatch(getListDocumentCategories(filter)),
    getSpecificDocumentCategories: id => dispatch(getSpecificDocumentCategories(id)),
    removeDocumentCategories: ids => dispatch(removeDocumentCategories(ids))
});

export default connect(mapStateToProps, mapDispatchToProps)(DocumentCategoryList);
